import { Box, Flex, HStack, Text, useToast } from "@chakra-ui/react";
import React, { Children, cloneElement, isValidElement } from "react";
import { useNavigate } from 'react-router-dom';

import { OnlineSubscriptionDto } from "@bcasmart/doxecure-commons/doxecureClientTypes";
import { Nullable } from "foundation-ts/types";

import { MobileTopBar } from "@components/MobileTopBar/MobileTopBar";
import { Sidebar } from "@components/Sidebar/Sidebar";
import { config } from "@/config/config";
import { useAuthContext } from "@hooks/useAuthContext";
import { useColorContext } from "@hooks/useColorContext";
import { useSubscription } from "@/hooks/useSubscription";
import { logout } from "@services/authentication.service";
import { $g1Color } from "@/theme/G1Style";
import { isAdminUser } from "@utils/functions";
import { G1Icon } from "@/utils/icons";
import { defaultErrorToast } from "@utils/toast";
import { UserProfile } from "@bcasmart/doxecure-commons/doxecureTypes";

type AuhenticatedLayoutProps = {
    children: React.ReactNode ;
    adminPage?: boolean;
    searchPage?: boolean;
    activeMenuItem?: string;
};

export const AuthenticatedLayout = (props: AuhenticatedLayoutProps) => {
    const { children, adminPage, searchPage, activeMenuItem } = props;
    const { isAuthenticated, isUserAuthenticated, isAdminAuthenticated, connectedUser, disconnect } = useAuthContext();
    const { data: subscription } = useSubscription();
    const toast = useToast();
    const navigate = useNavigate();

    if (!isAuthenticated) {
        toast(defaultErrorToast("Vous n'êtes pas autorisé à accéder à cette page"));
        disconnect();
        navigate(adminPage ? config.admin.loginURL! : config.app.loginURL!);
    } else if (!!adminPage && (isUserAuthenticated || (isAdminAuthenticated && !isAdminUser(connectedUser?.userFlags?.profile)))) {
        toast(defaultErrorToast("Veuillez vous connecter en tant qu'administrateur"));
        disconnect();
        logout();
        navigate(adminPage ? config.admin.loginURL! : config.app.loginURL!);
    }

    const corporation = connectedUser?.corporations?.first();
    const showExpireCardAlert = connectedUser?.userFlags?.profile === UserProfile.AdministratorCorporate && (corporation?.mandate?.status === "expired" || corporation?.mandate?.status === "expiringSoon");
    const activeSubscription: Nullable<OnlineSubscriptionDto> = !!subscription?.offline ? null : subscription as OnlineSubscriptionDto;
    const enhancedChildren = Children.map(children, child => {
        if (isValidElement(child)) {
            return cloneElement(child, { 
                connectedUser,
                lockedAccount: activeSubscription?.lockedAccount
            });
        }
        return child;
    });

    const handleSearch = (searchString: string) => {
        navigate("/app/search", { state: { search: searchString } }) ;
    };

    useColorContext();
    return (
        <Box as="section" bg={$g1Color('global.bg')} px="0" py="0">
            <Box px={{ base: "0" }}>
                { !!activeSubscription?.lockedAccount?.exceedsUsersLimit && (
                    <HStack bg="red.50" borderRadius="12px" p={4} m={4} border="1px solid" borderColor="red.200" alignItems="center">
                        <G1Icon.Warning color="red" size={24} />
                        <Text fontSize="sm" color="red.700">
                            Le nombre de collaborateurs pouvant avoir accès à {config.app.title} a dépassé la limite permise. Vous ne pouvez réaliser aucune action tant que le gestionnaire n'a pas résolu le problème. Veuillez le contacter pour plus de renseignements.
                        </Text> 
                    </HStack>
                )}
                { !!activeSubscription?.lockedAccount?.canceledAndWaitingSessionEnds && (
                    <HStack bg="red.50" borderRadius="12px" p={4} m={4} border="1px solid" borderColor="red.200" alignItems="center">
                        <G1Icon.Warning color="red" size={24} />
                        <Text fontSize="sm" color="red.700">
                            L'abonnement à {config.app.title} a été résilié. Seuls les acteurs définis dans des sessions en attente d'approbations ou de signatures sont encore autorisés à y accéder. Néanmoins, il ne vous est plus possible de créer ni publier de nouvelles sessions.{" "}
                            L'abonnement prendra fin définitivement dès l'expiration de la dernière session de signature.
                        </Text> 
                    </HStack>
                )}
                { showExpireCardAlert && (
                    <HStack bg="red.50" borderRadius="12px" p={4} m={4} border="1px solid" borderColor="red.200" alignItems="center">
                        <G1Icon.Warning color="red" size={24} />
                        <Text fontSize="sm" color="red.700">
                            Votre carte bancaire {corporation?.mandate?.status === "expired" ? "est expirée" : "expire bientôt"}. Veuillez définir, depuis votre interface de gestion de l'organisation, une nouvelle carte bancaire ou un mandat SEPA afin de permettre{" "}
                            le bon déroulement du renouvellement de votre abonnement.
                        </Text> 
                    </HStack>
                )}
                <Flex h="100vh" flexDirection="column">
                    <MobileTopBar activeItemKey={activeMenuItem} adminPage={adminPage} onSearch={handleSearch} />
                    <Flex flex="1" overflow="hidden">
                        <Sidebar
                            minW="20%"
                            display={{ base: "none", md: "flex" }}
                            activeItemKey={activeMenuItem}
                            onSearch={handleSearch}
                            activesearch={searchPage}
                            adminPage={adminPage}
                        />
                        {enhancedChildren}
                    </Flex>
                </Flex>
            </Box>
        </Box>
    ) ;
} ;